import React, { useCallback, useRef } from 'react';
import type { AssetType } from '#/graphql/types';
import { useNavigate } from 'react-router-dom';
import List from '#/common/components/List';
import { gql } from '@apollo/client';
import { getAssetTypeList } from '#/graphql/queries';
import columns from '../AssetTypeDataGrid/columns';
import { Filter } from '#/common/components/DataGrid';
import useToast from '#/common/hooks/useToast';
import useAssetTypeDelete from '../../hooks/useDeleteAssetTypeMutation';
import { inUseStatuses } from '../AssetType.Mappings';
import { List as DxList } from 'devextreme-react/list';

const getAssetTypeListQuery = gql(getAssetTypeList);
export interface AssetTypeListProps {
  permanentFilters?: Filter[];
}

const AssetTypeList: React.FC<AssetTypeListProps> = (props) => {
  const { permanentFilters } = props;

  const navigate = useNavigate();
  const list = useRef<DxList | null>(null);
  const { confirm } = useToast();
  const { deleteAssetType } = useAssetTypeDelete();

  const deleteItem = useCallback(
    async (items?: AssetType[], clearSelectedItems?: () => void) => {
      if (!items?.length) return;

      const assetTypesToDelete = items.filter(
        (assetType) =>
          assetType.inUseStatus === inUseStatuses.newlyCreated.key &&
          !(assetType.thirdPartyId && assetType.thirdPartySource),
      );

      if (assetTypesToDelete.length < items.length) {
        const sure = await confirm(
          'error',
          `Some selected asset types have been in active status and can not be deleted.`,
        );
        if (!sure) return;
      }

      await deleteAssetType(assetTypesToDelete.map((item) => item.id).join(','));
      list.current && list.current.instance.reload();
      clearSelectedItems && clearSelectedItems();
    },
    [deleteAssetType],
  );

  const openAssetType = useCallback(
    (assetType: AssetType) => {
      navigate(`/assets/types/${assetType.id}`);
    },
    [navigate],
  );

  return (
    <List<AssetType>
      setRef={(ref) => (list.current = ref)}
      titleKey="name"
      onClick={openAssetType}
      getItemsQuery={getAssetTypeListQuery}
      permanentFilters={permanentFilters}
      bodyFields={[
        { key: 'category', label: 'Category' },
        { key: 'criticalityLevel', label: 'Criticality Level' },
      ]}
      columnList={columns}
      isActionDeleteOnly
      floatingButtonOnClick={deleteItem}
    />
  );
};

export default React.memo(AssetTypeList);
